import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@ordering/utils/styling';
import { Spinner } from '@ordering/components/ui/Spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        branded: 'bg-brand-primary-background text-brand-primary-foreground hover:bg-brand-primary/90',
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        icon: 'bg-transparent text-primary-foreground hover:opacity-50 transition-opacity',
        outline: 'border border-gray-300 bg-white text-black hover:bg-gray-100',
        ghost: 'bg-transparent hover:bg-gray-100',
        black: 'bg-black text-white hover:bg-black/90',
      },
      size: {
        sm: 'rounded-[8px] px-4 py-2',
        lg: 'h-[48px] rounded-[8px] w-full',
        icon: 'h-[24px] w-[24xp]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'lg',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <Spinner />
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
